import { getToken, isSupported, onMessage } from "firebase/messaging";
import { getMessagingInstance, FCM_VAPID_KEY } from "../init-fcm";
import { registerPushToken } from "_services/user-service";
import { Store } from "_enums/enums";

/** Re-POST same FCM token to the API if last success is older than this (heals server-side deletes, metadata refresh). */
const PUSH_TOKEN_HEARTBEAT_MS = 3 * 24 * 60 * 60 * 1000;

let syncInFlight = false;
let resyncListenersAttached = false;
let foregroundMessageListenerAttached = false;

// const FCM_MSG_CACHE = "fcm-msg-ids";

// function fcmSeenRequestUrl(messageId) {
//   return `${window.location.origin}/__fcm_seen__/${encodeURIComponent(String(messageId))}`;
// }

/**
 * Same cache name as firebase-messaging-sw.js. SW writes a same-origin URL per messageId when it shows
 * a background notification; foreground onMessage checks before showing again (avoids duplicate after opening the app).
 */
// async function shouldSkipDuplicateFcmNotification(messageId) {
//   if (!messageId || typeof caches === "undefined") return false;
//   const url = fcmSeenRequestUrl(messageId);
//   const cache = await caches.open(FCM_MSG_CACHE);
//   const existing = await cache.match(url);
//   if (existing) return true;
//   await cache.put(new Request(url), new Response("1"));
//   return false;
// }

function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(Store.fcmDeviceId);
    if (!id) {
      id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(Store.fcmDeviceId, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * Request notification permission if needed, obtain FCM token, POST to backend.
 * Safe to call after login or on app load with an existing session.
 * No-ops when push is unavailable, permission denied, or user not authenticated.
 */
export async function syncPushTokenAfterAuth() {
  if (typeof window === "undefined") return;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

  const jwt = localStorage.getItem(Store.token);
  if (!jwt) return;

  if (syncInFlight) return;
  syncInFlight = true;

  try {
    if (!(await isSupported())) {
      return;
    }

    if (Notification.permission === "denied") {
      return;
    }
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return;
      }
    }

    if (!FCM_VAPID_KEY || !String(FCM_VAPID_KEY).trim()) {
      console.warn(
        "[push] Missing REACT_APP_FCM_VAPID_KEY. Add it to .env.local (Firebase → Project settings → Cloud Messaging → Web Push certificates)."
      );
      return;
    }

    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    const messaging = getMessagingInstance();

    const currentToken = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration
    });
    if (!currentToken) {
      return;
    }

    const userId = localStorage.getItem(Store.userId);
    const prevToken = localStorage.getItem(Store.fcmPushToken);
    const prevUser = localStorage.getItem(Store.fcmLastRegisteredUserId);
    const lastRegRaw = localStorage.getItem(Store.fcmLastRegisteredAt);
    const lastRegisteredMs = lastRegRaw ? parseInt(lastRegRaw, 10) : 0;
    const lastRegisteredOk = Number.isFinite(lastRegisteredMs) && lastRegisteredMs > 0;
    const needsHeartbeat = !lastRegisteredOk || Date.now() - lastRegisteredMs > PUSH_TOKEN_HEARTBEAT_MS;

    if (prevToken === currentToken && prevUser && userId && prevUser === userId && !needsHeartbeat) {
      return;
    }

    const deviceId = getOrCreateDeviceId();
    await registerPushToken({
      token: currentToken,
      platform: "web",
      deviceId,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent || "" : ""
    });

    localStorage.setItem(Store.fcmPushToken, currentToken);
    localStorage.setItem(Store.fcmLastRegisteredAt, String(Date.now()));
    if (userId) {
      localStorage.setItem(Store.fcmLastRegisteredUserId, userId);
    }
  } catch (err) {
    const msg = err && err.message ? err.message : typeof err === "string" ? err : String(err);
    const status = err && err.response && err.response.status;
    const data = err && err.response && err.response.data;
    console.warn("[push] Token sync skipped or failed:", msg, status ? `HTTP ${status}` : "", data != null ? data : "");
  } finally {
    syncInFlight = false;
  }
}

/**
 * Foreground FCM: show the same OS notification path as the service worker (`registration.showNotification`),
 * so notificationclick in firebase-messaging-sw.js handles taps. Page `Notification` is unreliable on mobile PWA.
 */
export function setupForegroundPushMessageHandler() {
  if (foregroundMessageListenerAttached || typeof window === "undefined") return;
  foregroundMessageListenerAttached = true;

  (async () => {
    try {
      if (!(await isSupported())) return;
      if (!FCM_VAPID_KEY || !String(FCM_VAPID_KEY).trim()) return;

      const messaging = getMessagingInstance();

      onMessage(messaging, async (payload) => {
        const n = payload.notification || {};
        const data = payload.data || {};

        const title = (n.title && String(n.title).trim()) || (data.title && String(data.title).trim()) || "Fiixit";
        const body = (n.body && String(n.body)) || (data.body && String(data.body)) || "";
        if (Notification.permission !== "granted") return;

        const origin = window.location.origin;
        const explicit = typeof data.clickUrl === "string" && data.clickUrl.trim() ? data.clickUrl.trim() : "";
        const clickUrl = explicit || `${origin}/Dashboard`;

        const messageId = payload.messageId || payload.fcmMessageId || data.fcmMessageId || "";
        const mergedData = Object.assign({}, data, { clickUrl });
        if (messageId) mergedData.fcmMessageId = String(messageId);

        const reg = await navigator.serviceWorker.ready;
        const iconUrl = (n.icon && String(n.icon).trim()) || `${origin}/favicon.ico`;
        const badgeUrl = `${origin}/favicon.ico`;

        const options = {
          body,
          data: mergedData,
          icon: iconUrl,
          badge: badgeUrl
        };
        if (data.ticketId) options.tag = `ticket-${data.ticketId}`;
        else if (messageId) options.tag = `fcm-${messageId}`;

        try {
          await reg.showNotification(title, options);
        } catch (err) {
          console.log("[push] Foreground showNotification failed:", err && err.message ? err.message : err);
        }
      });
    } catch (e) {
      console.log("[push] Foreground FCM listener not attached:", e && e.message ? e.message : e);
    }
  })();
}

/**
 * Re-run token sync when: tab visible again, network back, or service worker updated (FCM may rotate token).
 * Heartbeat in {@link syncPushTokenAfterAuth} re-POSTs periodically even if the token string is unchanged.
 */
export function setupPushTokenResyncListeners() {
  if (resyncListenersAttached || typeof document === "undefined") return;
  resyncListenersAttached = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      syncPushTokenAfterAuth();
    }
  });
  window.addEventListener("online", () => {
    syncPushTokenAfterAuth();
  });
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      syncPushTokenAfterAuth();
    });
  }
}
